import {
  ApolloLink,
  FetchResult,
  NextLink,
  Observable,
  Operation,
} from "apollo-link";

export default class OperationCounterLink extends ApolloLink {
  readonly bridge?: CypressBridge;
  totalRequestCount = 0; // used for correlating requests while debugging
  activeRequestCount = 0;

  constructor() {
    super();
    if (process.browser) {
      // TODO: initialise the bridge at cypress start-up rather than
      // ad-hoc where it is referenced
      window.cypressBridge = window.cypressBridge || {
        operationCount: 0,
        routeLoading: false,
      };
      this.bridge = window.cypressBridge;
      this.sync();
    }
  }

  /** Synchronise our local counter with with the bridge */
  sync() {
    if (this.bridge) {
      this.bridge.operationCount = this.activeRequestCount;
    }
  }

  startRequest() {
    this.totalRequestCount++;
    this.activeRequestCount += 1;
    this.sync();
  }

  endRequest() {
    this.activeRequestCount -= 1;
    this.sync();
  }

  public request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> {
    const nextObserver: Observable<FetchResult> = forward(operation);
    if (!this.bridge) {
      return nextObserver;
    }

    this.startRequest();

    // Subscribing to an observable executes it
    // To subscribe to nextObserver and return an observable we need to wrap it
    // See https://apollo-link-docs.netlify.app/docs/link/stateful/
    return new Observable<FetchResult>((observer) => {
      let subscription: ZenObservable.Subscription | null =
        nextObserver.subscribe({
          next: (result) => observer.next(result),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        });

      // The observable error/complete is not always propagated up the link chain.
      // But the unsubscribe hook is always called at the end of the operation,
      // so use that instead to indicate success
      return () => {
        if (subscription) {
          subscription.unsubscribe();
          subscription = null;

          this.endRequest();
        }
      };
    });
  }
}
